import { FC, useMemo } from 'react';
import { Field, FieldProps, Form, Formik, FormikProps } from 'formik';
import cx from 'classnames';

import { useCreatePlaceholder, useEditPlaceholder } from 'hooks/placeholders';

// constants & helpers
import { MAX_DESCRIPTION_LENGTH, MAX_TITLE_LENGTH, placeholderValidation } from './helpers';
import {
	getPlaceholderFormInitialValues,
	mapPlaceholderFormToCreateRequest,
	mapPlaceholderFormToUpdateRequest,
	PlaceholderFormValues,
} from '../placeholderMappers';

// components
import ButtonCross from 'shared/components/Buttons/ButtonCross';
import InputText from 'shared/components/FormInputs/InputText';
import ButtonDefault from 'shared/components/Buttons/ButtonsDefault';
import PlaceholderImageField from './PlaceholderImageField';
import DialogStyled from './DialogStyled';
import InputErrorHint from 'shared/components/InputErrorHint';
import Editor from 'shared/components/Editor';
import InlineDatePicker from 'shared/components/Pickers/InlineDatePicker';

// types
import { PlaceholderResponse } from '@joc/api-gateway';

// styles
import styles from './PlaceholderDialog.module.scss';

type PlaceholderDialogProps = {
	placeholderToEdit?: PlaceholderResponse;
	open: boolean;
	onClose: () => void;
};

export const PlaceholderDialog: FC<PlaceholderDialogProps> = ({ placeholderToEdit, open, onClose }) => {
	const closeDialog = () => onClose();

	const { createPlaceholder } = useCreatePlaceholder();
	const { editPlaceholder } = useEditPlaceholder();

	const initialValues = useMemo(() => getPlaceholderFormInitialValues(placeholderToEdit), [placeholderToEdit]);

	const submitClickHandler = (values: PlaceholderFormValues) => {
		if (placeholderToEdit?.id)
			editPlaceholder({
				id: placeholderToEdit.id,
				body: mapPlaceholderFormToUpdateRequest(values),
			});
		else
			createPlaceholder({
				body: mapPlaceholderFormToCreateRequest(values),
			});

		onClose();
	};

	const getPlainTextFromHtmlString = (html: string) => {
		const elem = document.createElement('div');
		elem.innerHTML = html;
		return elem.innerText;
	};

	return (
		<DialogStyled open={open} maxWidth="xl">
			<section className={styles.dialog}>
				<div className={styles.dialog__header}>
					<ButtonCross clickHandler={closeDialog} parentClassName={styles.dialog__header_button} />
				</div>
				<Formik
					enableReinitialize
					initialValues={initialValues}
					validationSchema={placeholderValidation}
					onSubmit={submitClickHandler}
					className={styles.dialog__form}
				>
					{({
						setFieldValue,
						setFieldTouched,
						dirty,
						isValid,
						errors,
						touched,
					}: FormikProps<PlaceholderFormValues>) => {
						const changeDateHandler = (field: string, value: Date) => {
							setFieldTouched(field, true);
							setFieldValue(field, value).then();
						};

						return (
							<Form className={styles.dialog__form}>
								<span className={styles.dialog__form_title}>
									{placeholderToEdit ? 'Edit' : 'Create'} Placeholder
								</span>
								<PlaceholderImageField name="imagePath" label="Photo" isRequired={false} />
								<Field name="name">
									{({ field }: FieldProps<string, PlaceholderFormValues>) => (
										<div className={styles.dialog__form_item}>
											<label className={styles.dialog__form_label} htmlFor="name">
												Placeholder name<span className={styles.dialog__form_required}>*</span>
											</label>
											<InputText
												id="name"
												field={field}
												placeholder="Placeholder name"
												setFieldValue={setFieldValue}
												parentClassName={styles.dialog__form_item__textfield}
											/>

											{(errors.name && touched.name && (
												<InputErrorHint errorText={errors.name} disableMargin={true} />
											)) || (
												<span
													className={cx(styles.dialog__form_limit, {
														[styles.dialog__form_limit_error]:
															field.value.trim().length > MAX_TITLE_LENGTH,
													})}
												>
													Maximum characters: {field.value.trim().length}/{MAX_TITLE_LENGTH}
												</span>
											)}
										</div>
									)}
								</Field>
								<Field name="description">
									{({ form, field }: FieldProps<string, PlaceholderFormValues>) => (
										<div className={styles.dialog__form_item}>
											<label className={styles.dialog__form_label} htmlFor="description">
												Description<span className={styles.dialog__form_required}>*</span>
											</label>
											<Editor
												fieldValue={field.value}
												onChange={value => form.setFieldValue('description', value)}
												onBlur={() => form.setFieldTouched(field.name, true)}
												placeholder="Description"
												isShowToolbar={false}
											/>
											{(errors.description && touched.description && (
												<InputErrorHint errorText={errors.description} disableMargin={true} />
											)) || (
												<span
													className={cx(styles.dialog__form_limit, {
														[styles.dialog__form_limit_error]:
															getPlainTextFromHtmlString(field.value).trim().length >
															MAX_DESCRIPTION_LENGTH,
													})}
												>
													Maximum characters:{' '}
													{getPlainTextFromHtmlString(field.value).trim().length}/
													{MAX_DESCRIPTION_LENGTH}
												</span>
											)}
										</div>
									)}
								</Field>
								<div className={styles.dialog__form_pickers}>
									<Field name="startDate">
										{({ field, meta }: FieldProps) => (
											<div
												className={cx(
													styles.dialog__form_item,
													styles.dialog__form_pickers_date
												)}
											>
												<label className={styles.dialog__form_label} htmlFor={field.name}>
													Start Date<span className={styles.dialog__form_required}>*</span>
												</label>

												<InlineDatePicker
													fieldName={field.name}
													fieldValue={field.value}
													setFieldValue={changeDateHandler}
													availableFuture={true}
												/>
												{meta.error && touched.startDate && (
													<InputErrorHint errorText={meta.error} disableMargin={true} />
												)}
											</div>
										)}
									</Field>
									<Field name="endDate">
										{({ field, meta }: FieldProps) => (
											<div
												className={cx(
													styles.dialog__form_item,
													styles.dialog__form_pickers_date
												)}
											>
												<label className={styles.dialog__form_label} htmlFor={field.name}>
													End Date<span className={styles.dialog__form_required}>*</span>
												</label>
												<InlineDatePicker
													fieldName={field.name}
													fieldValue={field.value}
													setFieldValue={changeDateHandler}
													availableFuture={true}
												/>
												{meta.error && touched.endDate && (
													<InputErrorHint errorText={meta.error} disableMargin={true} />
												)}
											</div>
										)}
									</Field>
								</div>
								<div className={styles.dialog__footer}>
									<ButtonDefault
										classList={['secondary', 'lg']}
										parentClassName={cx(
											styles.dialog__footer_button,
											styles.dialog__footer_cancel_button
										)}
										title="Cancel"
										clickHandler={closeDialog}
									/>
									<ButtonDefault
										submitType={true}
										classList={['primary', 'lg']}
										parentClassName={cx(styles.dialog__footer_button, {
											[styles.disable_submit]: !dirty || !isValid,
										})}
										title="Save"
										disabled={!dirty || !isValid}
									/>
								</div>
							</Form>
						);
					}}
				</Formik>
			</section>
		</DialogStyled>
	);
};
